import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {FmlxMotionControlPad} from 'fmlx-common-ui';
import {FastRobotErrorcode} from '../../constants/dataEnum';
import withIntl from '../../lib/withIntl';
import {fastCommonActuatorAPIErrorHandler} from '../../stores/business/errorHandlerBusiness';
import {hideLoading, showLoading} from '../../stores/actions/appStateAction';
import {
  forceMoveHorizontalCarrierRelative,
  forceMoveTrayRelative,
  forceMoveVerticalCarrierRelative,
  moveHorizontalCarrierRelativeWithExtendedProbe,
  moveTrayRelativeWithExtendedProbe,
  moveVerticalCarrierRelativeWithExtendedProbe,
} from '../../stores/business/instrumentBusiness';

import './MotionControlPad.scss';

function mapDispatchToProps(dispatch) {
  return {
    onShowLoading: bindActionCreators(showLoading, dispatch),
    onHideLoading: bindActionCreators(hideLoading, dispatch),
    onFastCommonActuatorAPIErrorHandler: bindActionCreators(fastCommonActuatorAPIErrorHandler, dispatch),
    instrumentBusinessMoveTrayRelativeWithExtendedProbe: bindActionCreators(
      moveTrayRelativeWithExtendedProbe,
      dispatch,
    ),
    instrumentBusinessMoveVerticalCarrierRelativeWithExtendedProbe: bindActionCreators(
      moveVerticalCarrierRelativeWithExtendedProbe,
      dispatch,
    ),
    instrumentBusinessMoveHorizontalCarrierRelativeWithExtendedProbe: bindActionCreators(
      moveHorizontalCarrierRelativeWithExtendedProbe,
      dispatch,
    ),
    instrumentBusinessMoveVerticalCarrierRelative: bindActionCreators(forceMoveVerticalCarrierRelative, dispatch),
    instrumentBusinessMoveHorizontalCarrierRelative: bindActionCreators(forceMoveHorizontalCarrierRelative, dispatch),
    instrumentBusinessMoveTrayRelative: bindActionCreators(forceMoveTrayRelative, dispatch),
  };
}

function MotionControlPad({
  onShowLoading,
  onHideLoading,
  onFastCommonActuatorAPIErrorHandler,
  onHandleError,
  intl,
  instrumentBusinessMoveTrayRelativeWithExtendedProbe,
  instrumentBusinessMoveVerticalCarrierRelativeWithExtendedProbe,
  instrumentBusinessMoveHorizontalCarrierRelativeWithExtendedProbe,
  instrumentBusinessMoveVerticalCarrierRelative,
  instrumentBusinessMoveHorizontalCarrierRelative,
  instrumentBusinessMoveTrayRelative,
  withExtendedTouchProbe,
  style,
  onClickArrow,
  moveValue,
  useDefaultErrorHandler,
}) {
  const [xyValue, setXyValue] = useState(moveValue);
  const [zValue, setZValue] = useState(moveValue);

  // #region call API modules: motor movement
  function moveHorizontalCarrier(currentXVal) {
    if (!currentXVal) return;

    onShowLoading(
      intl.formatMessage({
        id: 'trayCalibrationContainer.loading.moveHorizontalCarrier',
      }),
    );

    const moveHorizontalCarrierFunc = withExtendedTouchProbe
      ? instrumentBusinessMoveHorizontalCarrierRelativeWithExtendedProbe
      : instrumentBusinessMoveHorizontalCarrierRelative;

    moveHorizontalCarrierFunc(currentXVal).then((response) => {
      onHideLoading();
      if (response.result !== FastRobotErrorcode.NoError) {
        if (useDefaultErrorHandler) {
          onFastCommonActuatorAPIErrorHandler(response.result);
        } else {
          onHandleError(response.result);
        }
      }
      onClickArrow(response, 'x');
    });
  }

  function moveTray(currentYVal) {
    if (!currentYVal) return;

    onShowLoading(
      intl.formatMessage({
        id: 'trayCalibrationContainer.loading.moveTrayPosition',
      }),
    );

    const moveTrayFunc = withExtendedTouchProbe
      ? instrumentBusinessMoveTrayRelativeWithExtendedProbe
      : instrumentBusinessMoveTrayRelative;

    moveTrayFunc(currentYVal).then((response) => {
      // show UI information on FastRobotErrorCode !== None

      if (response.result !== FastRobotErrorcode.NoError) {
        if (useDefaultErrorHandler) {
          onFastCommonActuatorAPIErrorHandler(response.result);
        } else {
          onHandleError(response.result);
        }
      }

      onHideLoading();
      onClickArrow(response, 'y');
    });
  }

  function moveVerticalCarrier(currentZVal) {
    if (!currentZVal) return;

    onShowLoading(
      intl.formatMessage({
        id: 'trayCalibrationContainer.loading.moveTrayPosition',
      }),
    );

    const moveVerticalCarrierFunc = withExtendedTouchProbe
      ? instrumentBusinessMoveVerticalCarrierRelativeWithExtendedProbe
      : instrumentBusinessMoveVerticalCarrierRelative;

    moveVerticalCarrierFunc(currentZVal).then((response) => {
      if (response.result !== FastRobotErrorcode.NoError) {
        if (useDefaultErrorHandler) {
          onFastCommonActuatorAPIErrorHandler(response.result);
        } else {
          onHandleError(response.result);
        }
      }

      onHideLoading();
      onClickArrow(response, 'z');
    });
  }

  const handleControlPadButtonClick = (label) => {
    switch (label) {
      case `X-${xyValue}`:
        moveHorizontalCarrier(xyValue * -1);
        break;
      case `X+${xyValue}`:
        moveHorizontalCarrier(xyValue);
        break;
      case `Y-${xyValue}`:
        moveTray(xyValue * -1);
        break;
      case `Y+${xyValue}`:
        moveTray(xyValue);
        break;
      case `Z-${zValue}`:
        moveVerticalCarrier(zValue * -1);
        break;
      case `Z+${zValue}`:
        moveVerticalCarrier(zValue);
        break;
      default:
        break;
    }
  };

  const handleSetXyValue = (val) => {
    if (val > 999) {
      return;
    }

    let newVal = val;

    if (val === '00') {
      newVal = 0;
    }

    setXyValue(newVal);
  };

  const handleSetZValue = (val) => {
    if (val > 999) {
      return;
    }

    let newVal = val;

    if (val === '00') {
      newVal = 0;
    }

    setZValue(newVal);
  };

  return (
    <div className="motion-control-button" style={style}>
      <FmlxMotionControlPad
        showSlider={false}
        showMotorHoming={false}
        movementControlValue={{
          xy: {
            value: xyValue,
            onChange: handleSetXyValue,
          },
          z: {
            value: zValue,
            onChange: handleSetZValue,
          },
        }}
        tooltipDescription={{
          x: {
            left: `X-${xyValue}`,
            right: `X+${xyValue}`,
          },
          y: {
            top: `Y+${xyValue}`,
            bottom: `Y-${xyValue}`,
          },
          z: {
            top: `Z-${zValue}`,
            bottom: `Z+${zValue}`,
          },
        }}
        onMovementControlClick={handleControlPadButtonClick}
      />
      <span>
        <span className="motion-control-button__label label-x">X</span>
        <span className="motion-control-button__label label-y">Y</span>
        <span className="motion-control-button__label label-z">Z</span>
      </span>
    </div>
  );
}

MotionControlPad.propTypes = {
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onFastCommonActuatorAPIErrorHandler: PropTypes.func.isRequired,
  onHandleError: PropTypes.func,
  intl: PropTypes.any.isRequired,
  instrumentBusinessMoveTrayRelativeWithExtendedProbe: PropTypes.func.isRequired,
  instrumentBusinessMoveVerticalCarrierRelativeWithExtendedProbe: PropTypes.func.isRequired,
  instrumentBusinessMoveHorizontalCarrierRelativeWithExtendedProbe: PropTypes.func.isRequired,
  instrumentBusinessMoveVerticalCarrierRelative: PropTypes.func.isRequired,
  instrumentBusinessMoveHorizontalCarrierRelative: PropTypes.func.isRequired,
  instrumentBusinessMoveTrayRelative: PropTypes.func.isRequired,
  withExtendedTouchProbe: PropTypes.bool,
  style: PropTypes.object,
  moveValue: PropTypes.number,
  onClickArrow: PropTypes.func,
  useDefaultErrorHandler: PropTypes.bool,
};

MotionControlPad.defaultProps = {
  style: {},
  moveValue: 5,
  onClickArrow: () => {},
  onHandleError: () => {},
  withExtendedTouchProbe: true,
  useDefaultErrorHandler: true,
};

export default connect(() => ({}), mapDispatchToProps)(withIntl(MotionControlPad));
